import React, { useState, useEffect, useRef } from 'react';
import { Card, Form, Button, Alert, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { getKYCDetails, getKYCDocument, postApi, updateApi } from '../../api/api';
import PageTitle from '../layouts/PageTitle';
import Loading from '../components/Loading';
import swal from 'sweetalert2';

const KYC = ({ auth }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [kycStatus, setKycStatus] = useState(null);
    const [error, setError] = useState(null);
    const [formData, setFormData] = useState({
        fullName: '',
        dateOfBirth: '',
        address: '',
        city: '',
        state: '',
        country: '',
        pincode: '',
        documentType: 'passport',
        documentNumber: '',
    });
    
    const frontFileRef = useRef();
    const backFileRef = useRef();
    const [frontPreview, setFrontPreview] = useState(null);
    const [backPreview, setBackPreview] = useState(null);
    const [existingDocuments, setExistingDocuments] = useState({
        front: null,
        back: null
    });

    const [verificationStatus, setVerificationStatus] = useState({
        isEmailVerified: false,
        isMobileVerified: false
    });

    useEffect(() => {
        if (!auth.isAuthenticated || !auth.token) {
            navigate('/login', { replace: true });
            return;
        }
        
        checkKYCStatus();
    }, [auth, navigate]);

    const loadDocuments = async (kycData) => {
        try {
            if (kycData.documentFront) {
                const frontDoc = await getKYCDocument('front');
                setExistingDocuments(prev => ({
                    ...prev,
                    front: frontDoc
                }));
            }
            if (kycData.documentBack) {
                const backDoc = await getKYCDocument('back');
                setExistingDocuments(prev => ({
                    ...prev,
                    back: backDoc
                }));
            }
        } catch (error) {
            console.error('Error loading KYC documents:', error);
            swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load KYC documents'
            });
        }
    };

    const checkKYCStatus = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getKYCDetails();
            
            if (response.success) {
                const { kyc, userVerification } = response.data;
                setKycStatus({
                    status: kyc?.status || 'not_submitted',
                    submittedAt: kyc?.timeline?.submitted,
                    processedAt: kyc?.timeline?.processed,
                    updatedAt: kyc?.timeline?.updated,
                    remarks: kyc?.remarks
                });

                setVerificationStatus({
                    isEmailVerified: userVerification.isEmailVerified,
                    isMobileVerified: userVerification.isMobileVerified
                });

                if (kyc && kyc.status !== 'not_submitted') {
                    const dateOfBirth = kyc.dateOfBirth 
                        ? new Date(kyc.dateOfBirth).toISOString().split('T')[0]
                        : '';

                    setFormData({
                        fullName: kyc.fullName || '',
                        dateOfBirth: dateOfBirth,
                        address: kyc.address || '',
                        city: kyc.city || '',
                        state: kyc.state || '',
                        country: kyc.country || '',
                        pincode: kyc.pincode || '',
                        documentType: kyc.documentType || 'passport',
                        documentNumber: kyc.documentNumber || '',
                    });

                    await loadDocuments(kyc);
                }
            }
        } catch (error) {
            console.error('Error checking KYC status:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                if (type === 'front') {
                    setFrontPreview(reader.result);
                } else {
                    setBackPreview(reader.result);
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const validateForm = () => {
        const requiredFields = ['fullName', 'dateOfBirth', 'address', 'city', 'state', 'country', 'pincode', 'documentType', 'documentNumber'];
        const emptyFields = requiredFields.filter(field => !formData[field]);
        
        if (emptyFields.length > 0) {
            swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: `Please fill in all required fields: ${emptyFields.join(', ')}`
            });
            return false;
        }

        if (!kycStatus?.status || kycStatus.status === 'not_submitted') {
            if (!frontFileRef.current?.files[0] || !backFileRef.current?.files[0]) {
                swal.fire({
                    icon: 'error',
                    title: 'Validation Error',
                    text: 'Please upload both front and back documents'
                });
                return false;
            }
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            setSubmitting(true);
            setError(null);

            const formPayload = new FormData();
            Object.keys(formData).forEach(key => {
                formPayload.append(key, formData[key]);
            });

            if (frontFileRef.current?.files[0]) {
                formPayload.append('documentFront', frontFileRef.current.files[0]);
            }
            if (backFileRef.current?.files[0]) {
                formPayload.append('documentBack', backFileRef.current.files[0]);
            }

            const api = kycStatus?.status === 'rejected' ? updateApi : postApi;
            const response = await api('kyc', formPayload);

            if (response.success) {
                swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'KYC submitted successfully'
                }).then(() => {
                    checkKYCStatus();
                });
            } else {
                throw new Error(response.message || 'Failed to submit KYC');
            }
        } catch (error) {
            console.error('Error submitting KYC:', error);
            setError(error.message);
            swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message
            });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <Loading />;

    const isEditable = !kycStatus?.status || ['not_submitted', 'rejected'].includes(kycStatus.status);

    return (
        <>
            <PageTitle activeMenu="KYC Verification" motherMenu="Account" />

            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <Card.Title>KYC Verification</Card.Title>
                    <div className="d-flex align-items-center gap-3">
                        <div className="verification-status">
                            <Badge bg={verificationStatus.isEmailVerified ? 'success' : 'warning'}>
                                {verificationStatus.isEmailVerified ? 'Email Verified' : 'Email Not Verified'}
                            </Badge>
                            <Badge bg={verificationStatus.isMobileVerified ? 'success' : 'warning'} className="ms-2">
                                {verificationStatus.isMobileVerified ? 'Mobile Verified' : 'Mobile Not Verified'}
                            </Badge>
                        </div>
                        {kycStatus?.status && (
                            <div>
                                <Badge bg={
                                    kycStatus.status === 'approved' ? 'success' :
                                    kycStatus.status === 'pending' ? 'warning' :
                                    kycStatus.status === 'rejected' ? 'danger' : 'secondary'
                                }>
                                    {kycStatus.status.toUpperCase()}
                                </Badge>
                                {/* {kycStatus.status === 'pending' && kycStatus.submittedAt && (
                                    <small className="d-block text-muted mt-1">
                                        Submitted on: {new Date(kycStatus.submittedAt).toLocaleDateString()}
                                    </small>
                                )} */}
                            </div>
                        )}
                    </div>
                </Card.Header>
                <Card.Body>
                    {kycStatus?.status === 'rejected' && kycStatus.remarks && (
                        <Alert variant="danger" className="mb-4">
                            <strong>Rejection Reason:</strong> {kycStatus.remarks}
                            <br />
                            <small>
                                Processed on: {new Date(kycStatus.processedAt).toLocaleDateString()}
                            </small>
                        </Alert>
                    )}

                    {kycStatus?.status === 'approved' && kycStatus.processedAt && (
                        <Alert variant="success" className="mb-4">
                            <strong>KYC Approved</strong>
                            <br />
                            {/* <small>
                                Approved on: {new Date(kycStatus.processedAt).toLocaleDateString()}
                            </small> */}
                        </Alert>
                    )}

                    <Form onSubmit={handleSubmit}>
                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <Form.Group>
                                    <Form.Label>Full Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="fullName"
                                        value={formData.fullName}
                                        onChange={handleInputChange}
                                        disabled={!isEditable}
                                        required
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6 mb-3">
                                <Form.Group>
                                    <Form.Label>Date of Birth</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="dateOfBirth"
                                        value={formData.dateOfBirth}
                                        onChange={handleInputChange}
                                        disabled={!isEditable}
                                        required
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-12 mb-3">
                                <Form.Group>
                                    <Form.Label>Address</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        name="address"
                                        value={formData.address}
                                        onChange={handleInputChange}
                                        disabled={!isEditable}
                                        required
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6 mb-3">
                                <Form.Group>
                                    <Form.Label>City</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="city"
                                        value={formData.city}
                                        onChange={handleInputChange}
                                        disabled={!isEditable}
                                        required
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6 mb-3">
                                <Form.Group>
                                    <Form.Label>State</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="state"
                                        value={formData.state}
                                        onChange={handleInputChange}
                                        disabled={!isEditable}
                                        required
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6 mb-3">
                                <Form.Group>
                                    <Form.Label>Country</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="country"
                                        value={formData.country}
                                        onChange={handleInputChange}
                                        disabled={!isEditable}
                                        required
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6 mb-3">
                                <Form.Group>
                                    <Form.Label>Pincode</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="pincode"
                                        value={formData.pincode}
                                        onChange={handleInputChange}
                                        disabled={!isEditable}
                                        required
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6 mb-3">
                                <Form.Group>
                                    <Form.Label>Document Type</Form.Label>
                                    <Form.Select
                                        name="documentType"
                                        value={formData.documentType}
                                        onChange={handleInputChange}
                                        disabled={!isEditable}
                                        required
                                    >
                                        <option value="passport">Passport</option>
                                        <option value="driving_license">Driving License</option>
                                        <option value="national_id">National ID</option>
                                    </Form.Select>
                                </Form.Group>
                            </div>
                            <div className="col-md-6 mb-3">
                                <Form.Group>
                                    <Form.Label>Document Number</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="documentNumber"
                                        value={formData.documentNumber}
                                        onChange={handleInputChange}
                                        disabled={!isEditable}
                                        required
                                    />
                                </Form.Group>
                            </div>

                            {(isEditable || existingDocuments.front || existingDocuments.back) && (
                                <div className="row mt-4">
                                    <div className="col-md-6 mb-3">
                                        <Form.Group>
                                            <Form.Label>Document Front</Form.Label>
                                            {isEditable && (
                                                <Form.Control
                                                    type="file"
                                                    ref={frontFileRef}
                                                    onChange={(e) => handleFileChange(e, 'front')}
                                                    accept="image/*,.pdf"
                                                    required={!kycStatus?.status || kycStatus.status === 'not_submitted'}
                                                />
                                            )}
                                            {(frontPreview || existingDocuments.front) && (
                                                <div className="mt-2">
                                                    {frontPreview ? (
                                                        <img 
                                                            src={frontPreview} 
                                                            alt="Document Front" 
                                                            className="img-fluid" 
                                                            style={{ maxWidth: '200px' }} 
                                                        />
                                                    ) : (
                                                        <img 
                                                            src={existingDocuments.front} 
                                                            alt="Existing Document Front" 
                                                            className="img-fluid" 
                                                            style={{ maxWidth: '200px' }} 
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <Form.Group>
                                            <Form.Label>Document Back</Form.Label>
                                            {isEditable && (
                                                <Form.Control
                                                    type="file"
                                                    ref={backFileRef}
                                                    onChange={(e) => handleFileChange(e, 'back')}
                                                    accept="image/*,.pdf"
                                                    required={!kycStatus?.status || kycStatus.status === 'not_submitted'}
                                                />
                                            )}
                                            {(backPreview || existingDocuments.back) && (
                                                <div className="mt-2">
                                                    {backPreview ? (
                                                        <img 
                                                            src={backPreview} 
                                                            alt="Document Back" 
                                                            className="img-fluid" 
                                                            style={{ maxWidth: '200px' }} 
                                                        />
                                                    ) : (
                                                        <img 
                                                            src={existingDocuments.back} 
                                                            alt="Existing Document Back" 
                                                            className="img-fluid" 
                                                            style={{ maxWidth: '200px' }} 
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        </Form.Group>
                                    </div>
                                </div>
                            )}
                        </div>

                        {isEditable && (
                            <div className="text-center mt-4">
                                <Button 
                                    type="submit" 
                                    variant="primary" 
                                    disabled={submitting}
                                >
                                    {submitting ? 'Submitting...' : (
                                        kycStatus?.status === 'rejected' ? 'Update KYC' : 'Submit KYC'
                                    )}
                                </Button>
                            </div>
                        )}
                    </Form>
                </Card.Body>
            </Card>
        </>
    );
};

const mapStateToProps = (state) => ({
    auth: state.auth
});

export default connect(mapStateToProps)(KYC); 